/**
 * Error boundaries via `react-error-boundary` (R-18.62). Two thin wrappers around the
 * shared `ErrorFallback`:
 *
 * - `AppErrorBoundary` — the top-level boundary wrapping the whole app (R-18.59).
 * - `RouteErrorBoundary` — a per-route boundary so a single route's render failure does
 *   not blank the shell (R-18.60).
 *
 * Both report caught errors to the monitoring sink (R-18.64). Until a real logging
 * backend is wired (Sentry et al.), the sink is `console.error` — a single seam to
 * swap later.
 */

import { ErrorBoundary } from 'react-error-boundary';

import { ErrorFallback } from './ErrorFallback';

import { isStaleChunkError, reloadForStaleChunk } from '@/shared/lib/staleChunkReload';

function reportError(error: Error, info: { componentStack?: string | null }): void {
  // R-18.64 — error boundaries must report to the logging/monitoring system. Single
  // seam: swap console for the real reporter (Sentry) when observability is wired.
  console.error('[error-boundary]', error, info.componentStack);
  // A failed lazy-route import after a redeploy is not a code error — the chunk set
  // changed under us. React.lazy caches the rejection, so only a reload recovers.
  if (isStaleChunkError(error)) {
    reloadForStaleChunk();
  }
}

export function AppErrorBoundary({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={reportError}>
      {children}
    </ErrorBoundary>
  );
}

export function RouteErrorBoundary({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <ErrorBoundary
      fallbackRender={(props) => <ErrorFallback {...props} isRouteLevel />}
      onError={reportError}
    >
      {children}
    </ErrorBoundary>
  );
}
